import React, { useEffect } from 'react';

import { useQuery } from '@apollo/client';
import { Box, Container, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { scroller } from 'react-scroll';

import { JSBlock } from '../../components/JSBlock';
import JoinNowBlock from '../../components/JoinNowBlock/JoinNowBlock';
import { Loader } from '../../components/Loader';
import RouteWithCode from '../../components/RouteWithCode/RouteWithCode';
import { CardWrapper } from '../../components/UI/CardWrapper';
import { CategoryLabel } from '../../components/UI/CategoryLabel';
import { RM_TAGS } from '../../const';
import { useRmTag } from '../../context/RmContext';
import useScriptLoader, { SCRIPTS } from '../../context/ScriptLoader/useScriptLoader';
import { GET_PAGE_BY_URL } from '../../graphql/pages';
import {
  GET_PASSED_QUIZES_BY_PATHWAY,
  GET_PATHWAY_PAGE_INFO,
  GET_PATHWAY_PAGE_PROGRESS,
  GET_PATHWAYS_YOU_MIGHT_ALSO_LIKE,
  GET_VIDEOS_EXIST_IN_USER_WATCH_LIST,
} from '../../graphql/pathways';
import { generateImageKitUrl } from '../../modules/imageKit';
import Breadcrumbs from '../../pages/DiscoverPage/components/Breadcrumbs';
import { Header } from '../../pages/DiscoverPage/components/Header';
import theme, { colorsPalette } from '../../theme';
import {
  DashVideo,
  LearnerInfo,
  Pathway,
  Query,
  QueryGetPageByUrlArgs,
  QueryGetPassedQuizesResultsArgs,
  QueryGetPathwayLearnerPageInfoArgs,
  QueryGetPathwayPageInfoArgs,
  QueryListYouMightAlsoLikePathwaysArgs,
  QueryVideosExistInUserWatchListArgs,
  Quiz,
  QuizPassedResult,
} from '../../types';
import Cookie from '../../utils/cookies';
import {
  calcVideoProgressAndLinkToShow,
  collectValueByKey,
  countResultStars,
  downloadFile,
  generateCertificateName,
  getCertificatePDFUrl,
  secondsToHHHmmm,
  secondsToHms,
  translate,
} from '../../utils/helpers';
import { useAuth } from '../../utils/hooks';
import { CustomPage } from '../CustomPage';
import { DEFAULT_URL } from '../DiscoverPage/const';
import NotFoundPage from '../NotFoundPage';
import { LAST_VIEWED_PATHWAY_SLUG_COOKIE_NAME } from '../SingleVideoPage/const';
import { PathwayInformation } from './components/PathwayInformation';
import { PathwayPageSeoBlock } from './components/PathwayPageSeoBlock';
import { PathwayProgress } from './components/PathwayProgress';
import PathwayRating from './components/PathwayRating/PathwayRating';
import { PathwayTestBlock } from './components/PathwayTestBlock';
import { getCategories } from './components/PathwayVideoCard/PathwayVideoCard';
import PathwayVideos from './components/PathwayVideos';
import { PathwayWaitTestBlock } from './components/PathwayWaitTestBlock';
import { PathwaysBlock } from './components/PathwaysBlock';
import { TestSuccessBlock } from './components/TestSuccessBlock';
import { getPathwayPageJsonData, Test } from './const';
import useStyles, { useHeaderStyles } from './styles';

const NO_PATHWAY = 'no-pathway';

const NO_PATHWAY_TEST = [
  {
    id: NO_PATHWAY,
    title: translate('page.pathway.testComingSoon'),
    canPassTest: false,
    testPassed: false,
  },
];

const formatPathwayVideos = (videos, progressData, existsIn = {}) => {
  if (!videos || !videos.length) return [];

  return videos.map((video) => {
    const id = video?.id;
    const videoUrl = video?.video_url || id;
    const link = progressData[id] ? progressData[id].link : `/videos/${videoUrl}/`;

    return {
      ...video,
      link,
      user: video?.user?.name,
      userSlug: video?.user?.slug,
      duration: video?.duration ? secondsToHms(video?.duration) : 0,
      progress: progressData[id] ? progressData[id].progress : 0,
      completed: progressData[id] ? progressData[id].completed : false,
      new: progressData[id]?.new,
      isInWatchlist: existsIn[id],
    };
  });
};

const formatProgressData = (progress) => {
  if (!progress || !progress.length) return [];

  return progress?.reduce((all, elem) => {
    const id = elem?.video?.id;
    const videoUrl = elem?.video?.video_url || id;
    const { linkToShow, progressToShow } = calcVideoProgressAndLinkToShow(
      elem?.completed,
      elem?.last_position,
      elem?.video?.duration,
      videoUrl,
      elem?.chapter,
      elem?.time,
    );
    return {
      ...all,
      [id as any]: {
        link: linkToShow,
        progress: progressToShow,
        completed: elem?.completed,
        new: Boolean(elem?.video?.new),
      },
    };
  }, {});
};

export const getNavigationArray = (title, slug) => [
  {
    title: translate('page.discover.discover'),
    url: DEFAULT_URL,
  },
  {
    title: translate('page.pathway.pathways'),
    url: '/pathways',
  },
  {
    title,
    url: `/${slug}`,
  },
];

const generateTestsArr = (
  quizzes?: (Quiz | null)[] | null,
  availableQuizzes?: (Quiz | null)[] | null,
  quizzResults?: (QuizPassedResult | null)[] | null,
): Test[] => {
  let arr: Test[] = [];
  if (quizzes) {
    arr = quizzes.map((item, i) => ({
      id: item?.id,
      title: item?.title,
      canPassTest: !!(
        availableQuizzes && availableQuizzes.find((availableQuiz) => availableQuiz?.id === item?.id)
      ),
      testPassed: !!(quizzResults && quizzResults[i]?.passed),
    }));
  }

  return arr;
};

interface PathwayPageProps {
  userInfo?: LearnerInfo;
  tenantTitle?: string | null;
  tenantName?: string | null;
  tenantCircleUrl?: string | null;
  waitForUser?: boolean;
}

const notTestEnv = process.env.NODE_ENV === 'test';

const PathwayPage: React.FC<PathwayPageProps> = ({
  userInfo,
  tenantTitle,
  tenantName,
  tenantCircleUrl,
  waitForUser,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const header = useHeaderStyles();
  const params = useParams();
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const slug = params?.slug;
  const hasSubscription = !!(userInfo && userInfo.has_subscription);
  const isLoggedIn = !!userInfo;

  const urlHash =
    location?.pathname === '/recently-watched-my-watchlist/'
      ? 'my-watchlist'
      : location?.hash?.slice(1);

  useScriptLoader(SCRIPTS.TEAMS_SHARE);
  useRmTag(RM_TAGS.PathwayPage);

  const { data, loading } = useQuery<
    Pick<Query, 'getPathwayPageInfo'>,
    QueryGetPathwayPageInfoArgs
  >(GET_PATHWAY_PAGE_INFO, {
    variables: {
      filter: {
        slug,
      },
    },
    fetchPolicy: notTestEnv ? 'cache-first' : 'network-only',
  });

  const { data: pathwayProgressData, loading: loadingProgressData } = useQuery<
    Pick<Query, 'getPathwayLearnerPageInfo'>,
    QueryGetPathwayLearnerPageInfoArgs
  >(GET_PATHWAY_PAGE_PROGRESS, {
    variables: {
      filter: {
        slug,
      },
    },
    fetchPolicy: notTestEnv ? 'cache-first' : 'network-only',
    skip: !userInfo,
    ssr: notTestEnv,
  });

  const pathway = data?.getPathwayPageInfo?.pathway;
  const progress = pathwayProgressData?.getPathwayLearnerPageInfo?.progress;
  const videosIds = pathway?.videos
    ?.map((video) => video?.id)
    .filter((item): item is string => Boolean(item));

  const { data: videosInWatchlist } = useQuery<
    Pick<Query, 'VideosExistInUserWatchList'>,
    QueryVideosExistInUserWatchListArgs
  >(GET_VIDEOS_EXIST_IN_USER_WATCH_LIST, {
    variables: {
      video_ids: videosIds,
    },
    skip: !videosIds || !videosIds?.length || !userInfo,
  });

  const watchlistById =
    videosInWatchlist?.VideosExistInUserWatchList?.reduce(
      collectValueByKey('video_id', 'exists'),
      {},
    ) || {};

  const progressData = formatProgressData(progress);
  const pathwayVideos = formatPathwayVideos(pathway?.videos, progressData, watchlistById);
  const pathwayDuration = secondsToHHHmmm(pathway?.duration);
  const pathwayCount = pathway?.count_videos;
  const pathwayTitle = `${pathwayCount} ${t('common.videos')} • ${pathwayDuration}`;
  const categories = getCategories(pathway?.categories, pathway?.difficulty, false, 'blue');
  const navigationArr = getNavigationArray(pathway?.name, slug);
  const quizzIds = pathway?.quizes ? pathway.quizes.map((item) => Number(item?.id)) : [];

  const progressVideo = pathway?.videos
    ? pathway.videos.map((item) => {
        const videoFromProgress =
          progress && progress.find((video) => video?.video?.id === item?.id);
        return {
          video: {
            id: item?.id,
            title: item?.title,
          },
          completed: videoFromProgress?.completed,
        };
      })
    : [];

  const hasProgress = progress && progress.length > 0;

  const { data: quizzesResultData, loading: quizzesResultLoading } = useQuery<
    Pick<Query, 'GetPassedQuizesResults'>,
    QueryGetPassedQuizesResultsArgs
  >(GET_PASSED_QUIZES_BY_PATHWAY, {
    variables: {
      quiz_ids: quizzIds,
      pathway_id: Number(pathway?.id),
    },
    skip: !hasSubscription || loading,
    fetchPolicy: 'network-only',
  });

  const quizzesResults = quizzesResultData?.GetPassedQuizesResults;

  const tests = generateTestsArr(
    pathway?.quizes,
    pathwayProgressData?.getPathwayLearnerPageInfo?.pathway?.available_quizes,
    quizzesResults,
  );

  const { data: pageData, loading: pageLoading } = useQuery<
    Pick<Query, 'getPageByUrl'>,
    QueryGetPageByUrlArgs
  >(GET_PAGE_BY_URL, {
    variables: {
      input: {
        url: ['/pathways/'],
      },
    },
    skip: hasSubscription,
  });

  const page = pageData?.getPageByUrl;
  //filter seo-block to prevent conflicts with video page SEO
  const blocks = page?.blocks ? page.blocks.filter((item) => item?.type !== 'seo-block') : null;

  const { data: pathwaysData, loading: pathwaysLoading } = useQuery<
    Pick<Query, 'listYouMightAlsoLikePathways'>,
    QueryListYouMightAlsoLikePathwaysArgs
  >(GET_PATHWAYS_YOU_MIGHT_ALSO_LIKE, {
    variables: {
      filter: {
        slug: slug,
      },
    },
  });

  const pathways = pathwaysData?.listYouMightAlsoLikePathways;
  const showLoader =
    loading ||
    (loadingProgressData && userInfo) ||
    pathwaysLoading ||
    pageLoading ||
    quizzesResultLoading ||
    waitForUser;

  useEffect(() => {
    if (urlHash && !showLoader) {
      scroller.scrollTo(urlHash, {
        duration: 1000,
        delay: 150,
        smooth: 'easeInOutQuart',
        offset: -80,
      });
    }
  }, [urlHash, showLoader]);

  useEffect(() => {
    Cookie.set(LAST_VIEWED_PATHWAY_SLUG_COOKIE_NAME, slug);
  }, [slug]);

  if (!pathway && !loading) {
    return (
      <RouteWithCode code={404}>
        <NotFoundPage />
      </RouteWithCode>
    );
  }

  if (showLoader) {
    return (
      <>
        {pathway && <PathwayPageSeoBlock pathway={pathway} tenantTitle={tenantTitle} />}
        <Loader />
      </>
    );
  }

  const scriptData = {
    customScriptType: 'application/ld+json',
    js_code: getPathwayPageJsonData(pathway?.name, pathway?.description, pathway?.cpd_credits),
  };

  return (
    <>
      <PathwayPageSeoBlock pathway={pathway} tenantTitle={tenantTitle} />
      <JSBlock data={scriptData} />
      <Box>
        <Header title={''} imageUrl={pathway?.image_url} tenantName={tenantName}>
          <Container>
            {isLoggedIn && <Breadcrumbs navigation={navigationArr} />}
            <Typography variant="h1" className={header.title} data-testid="pathwayTitle">
              {pathway?.name}
            </Typography>
            <Typography className={header.description}>{pathway?.description}</Typography>
            <Box className={header.categoriesWrap}>
              <CategoryLabel
                key="pathway"
                name={t('page.pathway.pathway')}
                bgColor={colorsPalette.pink}
                textColor={theme.palette.secondary.dark}
                data-testid="pinkCategory"
              />
              {categories}
            </Box>
          </Container>
        </Header>
        <Container>
          <div className={classes.divider} />
        </Container>
        {!hasSubscription && (
          <JoinNowBlock
            isLoggedIn={isLoggedIn}
            classes={{
              container: classes.joinNowContainer,
            }}
            data-testid="joinNowBlock"
          />
        )}
        {hasSubscription && quizzesResults && quizzIds.length > 0 && (
          <Container id="certificate">
            {quizzIds.map((item, i) => {
              const quizResults = quizzesResults[i];
              const quiz = pathway?.quizes
                ? pathway.quizes.find((quizz) => Number(quizz?.id) === item)
                : null;

              const certName = generateCertificateName(tenantTitle, pathway?.name);
              const certUrl = generateImageKitUrl({
                path: getCertificatePDFUrl(quizResults?.hash, tenantName),
                transformation: undefined,
              });

              //show only passed quizzes
              if (quizResults && quiz && quizResults?.passed) {
                return (
                  <TestSuccessBlock
                    key={quiz?.id}
                    cpdCredits={pathway?.cpd_credits}
                    svgContent={pathway?.logo_svg}
                    circleUrl={tenantCircleUrl}
                    correctQuestions={quizResults?.count_correct_answers}
                    totalQuestions={quizResults?.count_of_questions}
                    percents={quizResults?.percentage_complete}
                    filledStars={countResultStars(quizResults?.percentage_complete, quiz?.stars)}
                    onDownloadCertificate={() => downloadFile(certName, certUrl)}
                    resultHash={quizResults?.hash}
                    pathwayName={pathway?.name}
                    passDate={quizResults?.date_passing}
                    category={pathway?.categories?.[0]?.title}
                  />
                );
              }
              return null;
            })}
          </Container>
        )}
        <Container className={classes.mainInfoWrapper}>
          <Box className={classes.leftPanel}>
            <PathwayInformation
              cpdCredits={pathway?.cpd_credits}
              showInfo
              wrapperClass={!hasProgress ? classes.pathwayInfoWrapper : ''}
              tenantCircleUrl={tenantCircleUrl}
              pathwaySvg={pathway?.logo_svg}
            />
            {isLoggedIn && (
              <PathwayProgress
                wrapperClass={hasProgress ? classes.pathwayProgressWrapper : ''}
                videos={progressVideo as DashVideo[]}
                hasSubscription={hasSubscription}
                tests={tests?.length > 0 ? tests : NO_PATHWAY_TEST}
              />
            )}
          </Box>
          <Box>
            <PathwayVideos
              videos={pathwayVideos}
              pathwayTitle={pathwayTitle}
              isLoggedIn={isLoggedIn}
            />
            {hasSubscription && (
              <CardWrapper>
                {tests?.length > 0 ? (
                  tests.map((item) => (
                    <PathwayTestBlock
                      key={item.id}
                      id={item.id}
                      canPassTest={item.canPassTest}
                      testPassed={item.testPassed}
                      title={item.title}
                      link={`${DEFAULT_URL}/pathways/${slug}/test/${item.id}`}
                      wrapperClass={classes.testBlockItem}
                    />
                  ))
                ) : (
                  <PathwayWaitTestBlock id={NO_PATHWAY} wrapperClass={classes.testBlockItem} />
                )}
              </CardWrapper>
            )}
            {isAuthenticated && <PathwayRating id={pathway?.id} />}
          </Box>
        </Container>
        {pathways && pathways.length > 0 && (
          <PathwaysBlock
            title={t('page.pathway.pathwaysYouMightLike')}
            pathways={pathways as Pathway[]}
          />
        )}
        {blocks && blocks.length > 0 && <CustomPage blocks={blocks} />}
      </Box>
    </>
  );
};

export default PathwayPage;
